import React, { Component } from 'react';
import { Row, Col, Card, CardBody, Spinner } from 'reactstrap';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from 'recharts';
import Axios from '../../apis/nodeServer';
import { withTranslation } from 'react-i18next';
import './riskKPIs.css';

class RiskKPIs extends Component {
    state = {
        kpiData: null,
        loading: true,
        error: null,
        periodDays: 90
    };

    componentDidMount() {
        this._isMounted = true;
        // Try to load real data, fallback to mock only if it's genuinely stuck
        this.loadKPIs();
        this.mockDataTimeout = setTimeout(() => {
            if (this._isMounted && (this.state.loading || this.state.error)) {
                console.log('📊 Using MOCK KPI data');
                this.generateMockKPIs();
            }
        }, 10000);
    }

    componentWillUnmount() {
        this._isMounted = false;
        if (this.mockDataTimeout) {
            clearTimeout(this.mockDataTimeout);
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.floorId !== this.props.floorId) {
            this.loadKPIs();
        }
    }

    generateMockKPIs = () => {
        const mockData = {
            summary: {
                total_locations: 36,
                average_risk_score: 42.50,
                critical_locations: 3,
                high_risk_locations: 8,
                medium_risk_locations: 15,
                low_risk_locations: 10
            },
            top_risk_locations: [
                {
                    location__description: 'Main Lab - Sampling Area',
                    location__room__room_name: 'Lab A',
                    total_risk_score: 95.2,
                    risk_level: 'Critical'
                },
                {
                    location__description: 'Processing Zone 1',
                    location__room__room_name: 'Lab B',
                    total_risk_score: 92.8,
                    risk_level: 'Critical'
                },
                {
                    location__description: 'Storage Area - Cold Room',
                    location__room__room_name: 'Storage A',
                    total_risk_score: 78.5,
                    risk_level: 'Critical'
                },
                {
                    location__description: 'Clean Room Entry',
                    location__room__room_name: 'Clean Room',
                    total_risk_score: 65.3,
                    risk_level: 'High'
                },
                {
                    location__description: 'Packaging Station 2',
                    location__room__room_name: 'Packaging',
                    total_risk_score: 58.7,
                    risk_level: 'High'
                }
            ],
            risk_trend: [
                { calculation_date__date: '2025-10-01', avg_score: 38.2 },
                { calculation_date__date: '2025-10-03', avg_score: 41.5 },
                { calculation_date__date: '2025-10-05', avg_score: 39.8 },
                { calculation_date__date: '2025-10-07', avg_score: 43.2 },
                { calculation_date__date: '2025-10-09', avg_score: 45.7 },
                { calculation_date__date: '2025-10-11', avg_score: 44.3 },
                { calculation_date__date: '2025-10-13', avg_score: 46.8 },
                { calculation_date__date: '2025-10-15', avg_score: 48.2 },
                { calculation_date__date: '2025-10-17', avg_score: 42.9 },
                { calculation_date__date: '2025-10-19', avg_score: 41.5 },
                { calculation_date__date: '2025-10-21', avg_score: 42.5 },
                { calculation_date__date: '2025-10-23', avg_score: 44.8 },
                { calculation_date__date: '2025-10-25', avg_score: 46.2 },
                { calculation_date__date: '2025-10-27', avg_score: 43.5 },
                { calculation_date__date: '2025-10-29', avg_score: 45.1 },
                { calculation_date__date: '2025-10-31', avg_score: 47.3 },
                { calculation_date__date: '2025-11-02', avg_score: 44.6 },
                { calculation_date__date: '2025-11-04', avg_score: 42.8 },
                { calculation_date__date: '2025-11-06', avg_score: 41.9 },
                { calculation_date__date: '2025-11-08', avg_score: 43.7 },
                { calculation_date__date: '2025-11-10', avg_score: 45.8 },
                { calculation_date__date: '2025-11-12', avg_score: 46.5 },
                { calculation_date__date: '2025-11-14', avg_score: 44.2 },
                { calculation_date__date: '2025-11-16', avg_score: 42.5 }
            ],
            risk_distribution: {
                Critical: 3,
                High: 8,
                Medium: 15,
                Low: 10
            }
        };

        this.setState({
            kpiData: mockData,
            loading: false,
            error: null
        });
    }

    loadKPIs = async () => {
        if (!this._isMounted) return;
        this.setState({ loading: true, error: null });

        try {
            const response = await Axios.instance.get('risk/kpis/', {
                params: {
                    period_days: this.state.periodDays,
                    ...(this.props.floorId ? { floor_id: this.props.floorId } : {})
                }
            });

            if (this._isMounted) {
                clearTimeout(this.mockDataTimeout);
                this.setState({
                    kpiData: response.data,
                    loading: false
                });
            }
        } catch (error) {
            console.error('Error loading KPIs:', error);
            // Don't show error immediately, let mock data timeout handle it
            if (this._isMounted) {
                this.setState({
                    error: 'Failed to load real data, using mock data...',
                    loading: true
                });
            }
        }
    };

    render() {
        const { kpiData, loading, error } = this.state;

        if (loading) {
            return (
                <div className="risk-kpi-container">
                    <div className="text-center">
                        <Spinner color="primary" />
                        <p className="mt-2">Loading KPIs...</p>
                    </div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="risk-kpi-container">
                    <div className="alert alert-danger">{error}</div>
                </div>
            );
        }

        if (!kpiData) return null;

        const { summary, top_risk_locations, risk_trend } = kpiData;

        // Dados para gráfico de pizza
        const pieData = [
            { name: 'Critical', value: summary.critical_locations, color: '#DC143C' },
            { name: 'High', value: summary.high_risk_locations, color: '#FF6347' },
            { name: 'Medium', value: summary.medium_risk_locations, color: '#FFA500' },
            { name: 'Low', value: summary.low_risk_locations, color: '#4A90E2' }
        ].filter(item => item.value > 0); // Remove categorias com 0

        const { t } = this.props;

        return (
            <div className="risk-kpi-container">
                <h4 className="kpi-section-title mb-4">{t('riskManagement.overview')}</h4>

                {/* KPI Cards */}
                <Row className="mb-4">
                    <Col md={3} sm={6} className="mb-3">
                        <Card className="kpi-card">
                            <CardBody>
                                <h6 className="kpi-title">{t('riskManagement.totalLocations')}</h6>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                                    <h2 className="kpi-value" style={{ margin: 0, fontSize: '36px', fontWeight: 'bold', lineHeight: '1' }}>{summary.total_locations}</h2>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#6c757d' }}>{t('riskManagement.monitored')}</span>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3} sm={6} className="mb-3">
                        <Card className="kpi-card">
                            <CardBody>
                                <h6 className="kpi-title">{t('riskManagement.averageRiskScore')}</h6>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                                    <h2 className="kpi-value" style={{ margin: 0, fontSize: '36px', fontWeight: 'bold', lineHeight: '1' }}>{summary.average_risk_score}</h2>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#6c757d' }}>{t('riskManagement.outOf100')}</span>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3} sm={6} className="mb-3">
                        <Card className="kpi-card kpi-card-critical">
                            <CardBody>
                                <h6 className="kpi-title">{t('riskManagement.criticalLocations')}</h6>
                                <h2 className="kpi-value" style={{ margin: 0, fontSize: '36px', fontWeight: 'bold', lineHeight: '1', color: '#dc3545' }}>{summary.critical_locations}</h2>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={3} sm={6} className="mb-3">
                        <Card className="kpi-card kpi-card-high">
                            <CardBody>
                                <h6 className="kpi-title">{t('riskManagement.highRiskLocations')}</h6>
                                <h2 className="kpi-value" style={{ margin: 0, fontSize: '36px', fontWeight: 'bold', lineHeight: '1', color: '#fd7e14' }}>{summary.high_risk_locations}</h2>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Charts Row */}
                <Row className="mb-4">
                    <Col md={6} className="mb-3">
                        <Card>
                            <CardBody>
                                <h5 className="chart-title">{t('riskManagement.riskDistribution')}</h5>
                                {pieData.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={100}
                                                label={(entry) => `${entry.name}: ${entry.value}`}
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <p className="text-center text-muted">No risk data available</p>
                                )}
                            </CardBody>
                        </Card>
                    </Col>

                    <Col md={6} className="mb-3">
                        <Card>
                            <CardBody>
                                <h5 className="chart-title">{t('riskManagement.riskTrend')} ({t('riskManagement.last30Days')})</h5>
                                {risk_trend && risk_trend.length > 0 ? (
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart data={risk_trend}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="calculation_date__date"
                                                tick={{ fontSize: 10 }}
                                                angle={-45}
                                                textAnchor="end"
                                                height={80}
                                            />
                                            <YAxis domain={[0, 100]} />
                                            <Tooltip />
                                            <Legend />
                                            <Line
                                                type="monotone"
                                                dataKey="avg_score"
                                                stroke="#8884d8"
                                                strokeWidth={2}
                                                name={t('riskManagement.averageRiskScore')}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <p className="text-center text-muted">No trend data available</p>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Top Risk Locations Table */}
                <Row>
                    <Col>
                        <Card>
                            <CardBody>
                                <h5 className="chart-title">{t('riskManagement.top5RiskLocations')}</h5>
                                {top_risk_locations && top_risk_locations.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead>
                                                <tr>
                                                    <th>{t('riskManagement.location')}</th>
                                                    <th>{t('riskManagement.room')}</th>
                                                    <th>{t('riskManagement.riskScore')}</th>
                                                    <th>{t('riskManagement.riskLevel')}</th>
                                                    <th>{t('riskManagement.contaminations')}</th>
                                                    <th>{t('riskManagement.frequency')}</th>
                                                    <th>{t('riskManagement.severity')}</th>
                                                    <th>{t('riskManagement.trend')}</th>
                                                    <th>{t('riskManagement.recurrence')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {top_risk_locations.map((loc, idx) => (
                                                    <tr key={idx}>
                                                        <td>{loc.location__description}</td>
                                                        <td>{loc.location__room__room_name}</td>
                                                        <td>
                                                            <strong>{loc.total_risk_score?.toFixed(2) ?? '0.00'}</strong>
                                                        </td>
                                                        <td>
                                                            <span className={`badge badge-${loc.risk_level?.toLowerCase() || 'low'}`}>
                                                                {loc.risk_level}
                                                            </span>
                                                        </td>
                                                        <td>{loc.contamination_count}</td>
                                                        <td>{loc.frequency_score?.toFixed(1) ?? '-'}</td>
                                                        <td>{loc.severity_score?.toFixed(1) ?? '-'}</td>
                                                        <td>{loc.trend_score?.toFixed(1) ?? '-'}</td>
                                                        <td>{loc.recurrence_score?.toFixed(1) ?? '-'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <p className="text-center text-muted">No risk locations found</p>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        );
    }
}

export default withTranslation()(RiskKPIs);

